import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { normalizeSymbol } from "../shared/utils";
import { Link, routes } from "wasp/client/router";
import { useAuth } from "wasp/client/auth";
import { getTradeById, updateTrade, uploadTradeImageDirect, resolveImageKeys, getTradeCustomFieldDefinitions, getTradeTagSuggestions, useQuery, useAction } from "wasp/client/operations";
import type { Trade, TradeTag } from "wasp/entities";

type TradeWithExitDate = Trade & { exitDate: Date | null };
import { ArrowLeft, Edit, Save, Star, X } from "lucide-react";
import { planHasPaidTier } from "../payment/plans";
import { Button } from "../client/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../client/components/ui/card";
import { Label } from "../client/components/ui/label";
import { Switch } from "../client/components/ui/switch";
import { toast } from "../client/hooks/use-toast";
import NotebookEditor from "../notebook/LazyNotebookEditor";
import TemplateManager from "../trading-note-templates/TemplateManager";
import TradingChart from "./LazyTradingChart";
import type { TradingNoteTemplate } from "wasp/entities";
import useTranslation from "../client/hooks/useTranslation";
import { markdownToHtml } from "../notebook/markdownToHtml";
import { htmlToMarkdown } from "../notebook/htmlToMarkdown";
import { compressImage } from "../notebook/imageCompression";
import { parseCustomFieldValue } from "./customFieldsUtils";
import { TagInput } from "./TagInput";
import { TradeTagSelector } from "./TradeTagSelector";
import { Input } from "../client/components/ui/input";

const formatDate = (date: Date): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

export default function TradeDetailPage() {
  const t = useTranslation();
  const { tradeId } = useParams<{ tradeId: string }>();
  const { data: user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [notes, setNotes] = useState(""); // Markdown
  const [htmlNotes, setHtmlNotes] = useState(""); // HTML pour l'éditeur
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();
  const [setupRating, setSetupRating] = useState<number | null>(null);
  const [isEditingCustomFields, setIsEditingCustomFields] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Array<{ fieldId: string; value: any }>>([]);

  const { data: trade, isLoading, refetch } = useQuery(
    getTradeById,
    { id: tradeId || "" },
    { enabled: !!tradeId }
  );
  const { data: customFieldDefs = [] } = useQuery(getTradeCustomFieldDefinitions);
  const { data: tagSuggestions = [] } = useQuery(getTradeTagSuggestions, { fieldId: null });
  const updateTradeFn = useAction(updateTrade);
  const uploadImageDirectFn = useAction(uploadTradeImageDirect);
  const resolveImageKeysFn = useAction(resolveImageKeys);

  useEffect(() => {
    if (trade && !isEditing) {
      // Only reset notes when not editing, to avoid overwriting template application
      const markdownNotes = trade.notes || "";
      setNotes(markdownNotes);
      // Convertir markdown en HTML pour l'éditeur
      setHtmlNotes(markdownToHtml(markdownNotes));
      setSetupRating((trade as any).setupRating ?? null);
    }
  }, [trade, isEditing]);

  // Initialiser les valeurs des champs custom depuis le trade
  useEffect(() => {
    if (trade && !isEditingCustomFields) {
      const tradeCustomValues = ((trade as any).TradeCustomFieldValue || []) as any[];
      const values = tradeCustomValues.map((cv: any) => {
        const fieldDef = (customFieldDefs as any[]).find((f: any) => f.id === cv.field?.id || f.id === cv.fieldId);
        if (!fieldDef) return null;
        return {
          fieldId: fieldDef.id,
          value: parseCustomFieldValue(fieldDef, cv),
        };
      }).filter(Boolean) as Array<{ fieldId: string; value: any }>;
      setCustomFieldValues(values);
    }
  }, [trade, customFieldDefs, isEditingCustomFields]);

  const updateCustomFieldValue = (fieldId: string, value: any) => {
    setCustomFieldValues((prev) => {
      const idx = prev.findIndex((cf) => cf.fieldId === fieldId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { fieldId, value };
        return next;
      }
      return [...prev, { fieldId, value }];
    });
  };

  const getCustomFieldValue = (fieldId: string) => {
    const cf = customFieldValues.find((cf) => cf.fieldId === fieldId);
    return cf ? cf.value : null;
  };

  const handleSaveCustomFields = async () => {
    if (!trade) return;
    try {
      await updateTradeFn({ id: trade.id, customFields: customFieldValues });
      toast({ title: t.tradeDetail.saveCustomFields });
      setIsEditingCustomFields(false);
      refetch();
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message, variant: "destructive" });
    }
  };

  const handleApplyTemplate = (template: TradingNoteTemplate) => {
    // If empty template (clearing selection), just clear the selection
    if (!template.id || template.id === "") {
      setSelectedTemplateId(undefined);
      return;
    }

    if (!isEditing) {
      // If not editing, just store the template ID for when editing starts
      setSelectedTemplateId(template.id);
      return;
    }

    // If editing, apply the template content
    // If there's existing content, append it after the template to preserve it
    const existingContent = notes && notes.trim() !== "" ? notes.trim() : "";
    let newContent = template.content;
    
    if (existingContent && existingContent !== template.content) {
      // Append existing content after template, separated by a divider
      newContent = `${template.content}\n\n---\n\n**${t.tradeDetail.previousContent}:**\n\n${existingContent}`;
    }
    
    setNotes(newContent);
    // Convertir markdown en HTML pour l'éditeur
    setHtmlNotes(markdownToHtml(newContent));
    setSelectedTemplateId(template.id);
  };

  const handleImageUpload = async (file: File): Promise<{ s3Key: string; downloadUrl: string }> => {
    if (!tradeId) {
      toast({ title: t.common.error, description: "Trade ID is required", variant: "destructive" });
      throw new Error("Trade ID is required");
    }

    try {
      const compressedFile = await compressImage(file);
      const fileData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === "string") {
            resolve(reader.result.split(",")[1]);
          } else {
            reject(new Error("Failed to read file"));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      const { s3Key, downloadUrl } = await uploadImageDirectFn({
        tradeId,
        fileName: compressedFile.name,
        fileType: compressedFile.type,
        fileData,
      });

      toast({ title: t.notebook.imageUploaded });
      return { s3Key, downloadUrl };
    } catch (error: any) {
      console.error("Error uploading image:", error);
      toast({
        title: t.notebook.imageUploadError,
        description: error.message || t.notebook.imageUploadError,
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleResolveImageKeys = async (s3Keys: string[]): Promise<Record<string, string>> => {
    const { resolved } = await resolveImageKeysFn({ s3Keys });
    return resolved;
  };

  const handleSaveNotes = async () => {
    if (!trade) return;

    try {
      // Convertir HTML en markdown pour sauvegarder
      const markdownNotes = htmlToMarkdown(htmlNotes);
      await updateTradeFn({
        id: trade.id,
        notes: markdownNotes || null,
        setupRating: setupRating,
      });
      setNotes(markdownNotes); // Synchroniser l'état markdown
      toast({
        title: t.tradeDetail.notesSaved,
        description: "Vos notes ont été sauvegardées avec succès.",
      });
      setIsEditing(false);
      refetch();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.tradeDetail.notesError,
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-10">
        <div className="text-center">{t.common.loading}</div>
      </div>
    );
  }

  if (!trade) {
    return (
      <div className="container mx-auto py-10">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Trade introuvable</h1>
          <Link to={routes.TradingJournalRoute.to}>
            <Button variant="outline">{t.tradeDetail.backToJournal}</Button>
          </Link>
        </div>
      </div>
    );
  }

  const tradeWithTags = trade as Trade & { tags?: TradeTag[] };

  return (
    <div className="container mx-auto py-10">
      <div className="mb-6">
        <Link to={routes.TradingJournalRoute.to}>
          <Button variant="ghost" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.tradeDetail.backToJournal}
          </Button>
        </Link>
        <h1 className="text-3xl font-bold">{t.tradeDetail.title}</h1>
        <p className="text-muted-foreground mt-2">
          {normalizeSymbol(tradeWithTags.symbol)} - {formatDate(new Date(trade.date))}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-6 mb-6">
        {/* Trading Chart — 4/6 */}
        <div className="lg:col-span-4 h-[480px]">
          <TradingChart
            symbol={trade.symbol}
            entryPrice={trade.entryPrice}
            exitPrice={trade.exitPrice}
            entryDate={new Date(trade.date)}
            exitDate={(trade as TradeWithExitDate).exitDate ? new Date((trade as TradeWithExitDate).exitDate!) : null}
            type={trade.type as "long" | "short"}
          />
        </div>

        {/* Informations principales — 2/6 */}
        <div className="lg:col-span-2 h-[480px]">
          <Card className="h-full flex flex-col">
            <CardHeader className="shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle>{t.tradeDetail.tradeInfo}</CardTitle>
              {(customFieldDefs as any[]).length > 0 && (
                isEditingCustomFields ? (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setIsEditingCustomFields(false); }}>
                      <X className="mr-2 h-4 w-4" />
                      {t.tradeDetail.cancel}
                    </Button>
                    <Button size="sm" onClick={handleSaveCustomFields}>
                      <Save className="mr-2 h-4 w-4" />
                      {t.tradeDetail.saveCustomFields}
                    </Button>
                  </div>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setIsEditingCustomFields(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    {t.tradeDetail.editCustomFields}
                  </Button>
                )
              )}
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.date}</Label>
                <p className="font-medium">{formatDate(new Date(trade.date))}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.symbol}</Label>
                <p className="font-medium">{normalizeSymbol(trade.symbol)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.type}</Label>
                <span
                  className={`inline-block px-2 py-1 rounded text-sm ${
                    trade.type === "long"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {trade.type === "long" ? t.journal.long : t.journal.short}
                </span>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.status}</Label>
                <span
                  className={`inline-block px-2 py-1 rounded text-sm ${
                    (trade.status as string) === "open"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {(trade.status as string) === "open" ? t.journal.open : t.journal.closed}
                </span>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">
                  {t.tradeDetail.entryPrice}
                </Label>
                <p className="font-medium">
                  {trade.entryPrice < 10 
                    ? trade.entryPrice.toFixed(5) 
                    : trade.entryPrice.toFixed(2)} $
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">
                  {t.tradeDetail.exitPrice}
                </Label>
                <p className="font-medium">
                  {trade.exitPrice 
                    ? (trade.exitPrice < 10 
                        ? trade.exitPrice.toFixed(5) 
                        : trade.exitPrice.toFixed(2)) + " $"
                    : "-"}
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.quantity}</Label>
                <p className="font-medium">{trade.quantity.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.pnl}</Label>
                <p
                  className={`font-medium ${
                    trade.pnl === null || trade.pnl === undefined
                      ? "text-muted-foreground"
                      : trade.pnl >= 0
                      ? "text-pnlPositive"
                      : "text-pnlNegative"
                  }`}
                >
                  {trade.pnl === null || trade.pnl === undefined
                    ? "-"
                    : `${trade.pnl >= 0 ? "+" : ""}${trade.pnl < 10 && trade.pnl > -10 
                        ? trade.pnl.toFixed(5) 
                        : trade.pnl.toFixed(2)} $`}
                </p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.tradeDetail.commission}</Label>
                <p className="font-medium">{trade.commission.toFixed(2)} $</p>
              </div>
            </div>

            <div>
              <Label className="text-sm text-muted-foreground block mb-2">{t.journal.tags}</Label>
              <TradeTagSelector
                selectedTagIds={(tradeWithTags.tags ?? []).map((tag) => tag.id)}
                onChange={async (tagIds) => {
                  try {
                    await updateTradeFn({ id: trade.id, tagIds });
                    refetch();
                  } catch (err: any) {
                    toast({
                      title: t.journal.tagCreateError,
                      description: err?.message,
                      variant: "destructive",
                    });
                  }
                }}
              />
            </div>

            {/* Champs personnalisés */}
            {(customFieldDefs as any[]).length > 0 && (
              <div className="pt-4 border-t space-y-4">
                <Label className="text-base font-semibold">{t.tradeDetail.customFields}</Label>
                {(customFieldDefs as any[]).map((field: any) => {
                  const currentValue = getCustomFieldValue(field.id);

                  if (!isEditingCustomFields) {
                    // Mode lecture
                    let displayValue: string = t.tradeDetail.noValue;
                    if (currentValue !== null && currentValue !== undefined) {
                      if (field.type === "boolean") {
                        displayValue = currentValue ? "✓" : "✗";
                      } else if (field.type === "date" && currentValue) {
                        displayValue = formatDate(new Date(currentValue));
                      } else if (field.type === "tags" && Array.isArray(currentValue)) {
                        displayValue = currentValue.length > 0 ? currentValue.join(", ") : t.tradeDetail.noValue;
                      } else if (currentValue !== "" && currentValue !== null) {
                        displayValue = String(currentValue);
                      }
                    }
                    return (
                      <div key={field.id}>
                        <Label className="text-sm text-muted-foreground block mb-1">{field.label}</Label>
                        {field.type === "tags" && Array.isArray(currentValue) && currentValue.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {currentValue.map((tag: string) => (
                              <span key={tag} className="px-2 py-0.5 rounded text-xs bg-primary/10 text-primary">{tag}</span>
                            ))}
                          </div>
                        ) : (
                          <p className="font-medium">{displayValue}</p>
                        )}
                      </div>
                    );
                  }

                  // Mode édition
                  if (field.type === "tags") {
                    const tags = Array.isArray(currentValue) ? currentValue : [];
                    const fieldSuggestions = (Array.isArray(tagSuggestions) ? tagSuggestions : []).filter(
                      (s: any) => s.fieldId === field.id || !s.fieldId
                    );
                    return (
                      <div key={field.id}>
                        <Label htmlFor={`custom-${field.id}`}>{field.label}{field.isRequired && " *"}</Label>
                        <TagInput
                          tags={tags}
                          suggestions={fieldSuggestions.map((s: any) => s.name)}
                          onTagsChange={(newTags) => updateCustomFieldValue(field.id, newTags)}
                          placeholder={`Ajouter des tags...`}
                        />
                      </div>
                    );
                  } else if (field.type === "text") {
                    return (
                      <div key={field.id}>
                        <Label htmlFor={`custom-${field.id}`}>{field.label}{field.isRequired && " *"}</Label>
                        <Input
                          id={`custom-${field.id}`}
                          value={currentValue || ""}
                          onChange={(e) => updateCustomFieldValue(field.id, e.target.value)}
                          required={field.isRequired}
                        />
                      </div>
                    );
                  } else if (field.type === "number") {
                    return (
                      <div key={field.id}>
                        <Label htmlFor={`custom-${field.id}`}>{field.label}{field.isRequired && " *"}</Label>
                        <Input
                          id={`custom-${field.id}`}
                          type="number"
                          step="0.01"
                          value={currentValue ?? ""}
                          onChange={(e) => updateCustomFieldValue(field.id, e.target.value ? parseFloat(e.target.value) : null)}
                          required={field.isRequired}
                        />
                      </div>
                    );
                  } else if (field.type === "date") {
                    return (
                      <div key={field.id}>
                        <Label htmlFor={`custom-${field.id}`}>{field.label}{field.isRequired && " *"}</Label>
                        <Input
                          id={`custom-${field.id}`}
                          type="date"
                          value={currentValue ? new Date(currentValue).toISOString().split("T")[0] : ""}
                          onChange={(e) => updateCustomFieldValue(field.id, e.target.value ? new Date(e.target.value) : null)}
                          required={field.isRequired}
                        />
                      </div>
                    );
                  } else if (field.type === "boolean") {
                    return (
                      <div key={field.id} className="flex items-center gap-2">
                        <Switch
                          id={`custom-${field.id}`}
                          checked={currentValue === true}
                          onCheckedChange={(v) => updateCustomFieldValue(field.id, v)}
                        />
                        <Label htmlFor={`custom-${field.id}`} className="cursor-pointer">
                          {field.label}{field.isRequired && " *"}
                        </Label>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            )}

            {planHasPaidTier(user?.subscriptionPlan) && (
              <div>
                <Label className="text-sm text-muted-foreground block mb-1">{t.journal.setupRating}</Label>
                <div className="flex items-center gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={async () => {
                        const newRating = setupRating === star ? null : star;
                        setSetupRating(newRating);
                        try {
                          await updateTradeFn({ id: trade.id, setupRating: newRating });
                          refetch();
                        } catch (error: any) {
                          setSetupRating(setupRating);
                          toast({ title: t.common.error, description: error.message, variant: "destructive" });
                        }
                      }}
                      className="focus:outline-none transition-transform hover:scale-110"
                    >
                      <Star
                        className={`h-6 w-6 ${
                          setupRating !== null && star <= setupRating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-muted-foreground"
                        }`}
                      />
                    </button>
                  ))}
                  {setupRating !== null && (
                    <span className="text-xs text-muted-foreground ml-2">{setupRating}/5</span>
                  )}
                </div>
              </div>
            )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Notes — pleine largeur */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t.tradeDetail.notes}</CardTitle>
            {!isEditing ? (
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Edit className="mr-2 h-4 w-4" />
                {t.tradeDetail.edit}
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setIsEditing(false);
                  const markdownNotes = trade.notes || "";
                  setNotes(markdownNotes);
                  setHtmlNotes(markdownToHtml(markdownNotes));
                  setSetupRating((trade as any).setupRating ?? null);
                  setSelectedTemplateId(undefined);
                }}>
                  <X className="mr-2 h-4 w-4" />
                  {t.tradeDetail.cancel}
                </Button>
                <Button size="sm" onClick={handleSaveNotes}>
                  <Save className="mr-2 h-4 w-4" />
                  {t.tradeDetail.save}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <TemplateManager
                  onSelectTemplate={handleApplyTemplate}
                  selectedTemplateId={selectedTemplateId}
                />
              </div>
              <NotebookEditor
                content={htmlNotes}
                onChange={(html) => {
                  setHtmlNotes(html);
                  setNotes(htmlToMarkdown(html));
                }}
                onImageUpload={handleImageUpload}
                resolveImageKeys={handleResolveImageKeys}
                placeholder={t.notebook.editorPlaceholder}
              />
            </div>
          ) : (
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {trade.notes ? (
                <NotebookEditor
                  content={markdownToHtml(trade.notes)}
                  onChange={() => {}}
                  resolveImageKeys={handleResolveImageKeys}
                  placeholder=""
                  readOnly={true}
                />
              ) : (
                <p className="text-muted-foreground italic">{t.tradeDetail.noNotes}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

